import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Loader2, CreditCard, CheckCircle, AlertTriangle } from 'lucide-react';
import { stripeApi } from '@/lib/api';

// Initialize Stripe - use the publishable key from environment or fallback
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 
  'pk_live_51SyFGN422yZ51y1Jx9dM3nnkPZkhPktb7HryXInto9MEdWj8o9ZQVKJUYq23cp9lG9GyJoCnZP6uwiq3dwR8B9rZ00NwzFD5LV'
);

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#111827',
      fontFamily: 'Manrope, sans-serif',
      fontWeight: '500',
      lineHeight: '24px',
      '::placeholder': {
        color: '#6b7280',
      },
      iconColor: '#FF4500',
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
  hidePostalCode: true,
};

function CardForm({ onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [setupIntentId, setSetupIntentId] = useState(null);

  useEffect(() => {
    // Create setup intent when component mounts
    const createSetupIntent = async () => {
      try {
        const response = await stripeApi.createSetupIntent();
        setSetupIntentId(response.data.setup_intent_id);
      } catch (error) {
        console.error('Failed to create setup intent:', error);
        setCardError('Failed to initialize payment form. Please try again.');
      }
    };
    createSetupIntent();
  }, []);

  const handleCardChange = (event) => {
    setCardError(event.error ? event.error.message : null);
    setCardComplete(event.complete);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setCardError(null);

    try {
      // Get card element
      const cardElement = elements.getElement(CardElement);
      
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setCardError(error.message);
        setLoading(false);
        return;
      }

      // Call our API to confirm and store the payment method
      // Note: This requires the user to be logged in, so it's called after registration
      onSuccess(paymentMethod.id, setupIntentId);
      
    } catch (error) {
      setCardError(error.message || 'Something went wrong');
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-muted/50 rounded-sm border border-border">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="w-5 h-5 text-primary" />
          <span className="font-medium text-sm">Payment Details</span>
        </div>
        
        <div className="bg-white p-4 rounded-sm border-2 border-gray-200 shadow-sm">
          <CardElement 
            options={cardElementOptions}
            onChange={handleCardChange}
          />
        </div>
        
        {cardError && (
          <div className="flex items-center gap-2 mt-2 text-red-500 text-sm">
            <AlertTriangle className="w-4 h-4" />
            {cardError}
          </div>
        )}
        
        <p className="text-xs text-muted-foreground mt-3">
          Your card will <strong>NOT be charged</strong> during your 14-day free trial.
          After the trial, you'll be billed $9.95/week. Cancel anytime before to avoid charges.
        </p>
      </div>

      <div className="flex gap-3">
        <Button
          type="submit"
          className="w-full btn-active"
          disabled={!stripe || loading || !cardComplete}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving Card...
            </>
          ) : cardComplete ? (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Save Card & Start Trial
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              Enter Card Details
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

export default function CardSetupForm({ onSuccess, onError }) {
  return (
    <Elements stripe={stripePromise}>
      <CardForm 
        onSuccess={onSuccess} 
        onError={onError}
      />
    </Elements>
  );
}
